import React from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Layers, GitCompareArrows, CalendarDays, FlaskConical } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

/** Small "smart" widgets shared by Home and Profile: greeting, weekly activity bars, quick actions, upcoming tasks. */
export function Greeting({ name }) {
  const { t } = useLang();
  const h = new Date().getHours();
  const key = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
  return (
    <div data-testid="greeting">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0b0f1a]">{t(`smart.${key}`)}{name ? `, ${name.split(" ")[0]}` : ""} <span aria-hidden="true" data-no-invert className="inline-block">👋</span></h1>
      <p className="mt-1 text-slate-500">{t("smart.tagline")}</p>
    </div>
  );
}

export function ActivityBars({ data = [], className = "" }) {
  const { t } = useLang();
  const max = Math.max(1, ...data);
  const thisWeek = data.reduce((a, b) => a + b, 0);
  const days = t("smart.days");
  const todayIdx = new Date().getDay();
  return (
    <div className={`rounded-3xl border border-slate-200 cpenz-glass-light p-5 ${className}`} data-testid="activity-bars">
      <div className="flex items-baseline justify-between">
        <p className="text-[10px] font-semibold tracking-wider uppercase text-slate-500">{t("smart.progress")}</p>
        <span className="text-xs font-semibold text-emerald-600" data-testid="activity-total">{thisWeek} {t("smart.actions")}</span>
      </div>
      <div className="mt-4 flex items-end gap-2 h-16" role="img" aria-label={`${thisWeek} ${t("smart.actions")}`}>
        {data.map((v, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full rounded-full transition-[height] duration-700" style={{ height: `${Math.max(8, (v / max) * 52)}px`, background: v ? "linear-gradient(180deg,#7c3aed,#3b82f6)" : "#e9ecf3" }} />
            <span className="text-[9px] text-slate-400">{days[(todayIdx - 6 + i + 7) % 7]}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">{t("smart.this_week")}</p>
    </div>
  );
}

export function QuickActions({ compareTo }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const acts = [
    ["qa-new", Plus, t("smart.qa_new"), () => { document.querySelector('[data-testid="home-capture-input"]')?.focus(); window.scrollTo({ top: 0, behavior: "smooth" }); }],
    ["qa-vault", Layers, t("smart.qa_vault"), () => navigate("/app/dashboard")],
    ["qa-evidence", FlaskConical, t("smart.qa_evidence"), () => navigate("/app/dashboard?filter=exploring")],
    ["qa-compare", GitCompareArrows, t("smart.qa_compare"), () => (compareTo ? navigate(compareTo) : document.querySelector('[data-testid="home-connections"]')?.scrollIntoView({ behavior: "smooth" }))],
  ];
  return (
    <div data-testid="quick-actions">
      <p className="text-[10px] font-semibold tracking-wider uppercase text-slate-500">{t("smart.quick")}</p>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {acts.map(([id, Icon, label, fn]) => (
          <button key={id} type="button" data-testid={id} onClick={fn} className="group rounded-2xl border border-slate-200 bg-white px-3 py-3 flex sm:flex-col items-center gap-3 sm:gap-2 hover:border-[#7c3aed] hover:-translate-y-0.5 transition-[transform,border-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed]">
            <span className="h-9 w-9 rounded-xl bg-[#f4f6fa] text-[#7c3aed] inline-flex items-center justify-center group-hover:bg-[#7c3aed] group-hover:text-white transition-colors"><Icon size={16} /></span>
            <span className="text-xs sm:text-[11px] font-semibold text-[#0b0f1a] text-left sm:text-center leading-tight">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function UpcomingTasks({ items = [] }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  return (
    <div className="rounded-3xl border border-slate-200 cpenz-glass-light p-5" data-testid="upcoming-tasks">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold tracking-wider uppercase text-slate-500 inline-flex items-center gap-1.5"><CalendarDays size={12} /> {t("smart.upcoming")}</p>
        <span className="text-xs text-slate-400">{items.length}</span>
      </div>
      {items.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">{t("smart.upcoming_empty")}</p>
      ) : (
        <ul className="mt-3 divide-y divide-slate-100">
          {items.map((tk) => (
            <li key={tk.id}>
              <button type="button" data-testid={`upcoming-${tk.id}`} onClick={() => navigate(`/app/project/${tk.project_id}`)} className="w-full text-left py-2.5 flex items-start gap-3 hover:bg-[#f8f9fc] rounded-xl px-1 -mx-1 min-h-[44px]">
                <span aria-hidden="true" className="mt-1 h-4 w-4 rounded-full border-2 border-[#7c3aed]/50 shrink-0" />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-[#0b0f1a] truncate">{tk.title}</span>
                  <span className="block text-xs text-slate-500 truncate">{tk.project_name}{tk.date ? ` · ${new Date(tk.date).toLocaleDateString(lang === "IT" ? "it-IT" : "en-GB", { day: "numeric", month: "short" })}` : ""}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
